import { uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import { languageLabel, testTitle } from '@boViews/shared/testDisplay';
import TestStatusBadge from '@boViews/shared/TestStatusBadge';
import { faCheck, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Loader from '@sharedComponents/Loader';
import SearchBar from '@sharedComponents/SearchBar';
import Typography from '@sharedComponents/Typography';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { collectionEntry, matchParts, RESULT_LIMIT } from '../testRules';

import * as style from './style.module.css';

const MIN_TERM_LENGTH = 2;
const NO_ANSWER = { term: null, tests: [], failed: false };

const noTests = async () => [];
const noAddition = async () => {};

// Text with the words of the search in bold, at the size of the text.
const Highlighted = ({ text, term, color, fontSize = 'm' }) => (
    <>
        {matchParts(text, term).map((part, index) =>
            part.match ? (
                <Typography key={index} span fontWeight="bold" fontSize={fontSize} color={color}>
                    {part.text}
                </Typography>
            ) : (
                <Fragment key={index}>{part.text}</Fragment>
            ),
        )}
    </>
);

/**
 * Search of a test to add to the collection, by reference or title, from two characters: each result shows its
 * reference and its title, the words of the search in bold, its level, its language and its status. The tests of
 * `collectionTests` are checked, with their status as the collection holds it. Only the first RESULT_LIMIT results
 * are shown: beyond, a message invites to refine the search. `languagesOptions` are the catalogue languages (their
 * id labelled by their code). `onSearch(term, { signal })` resolves to the matching tests and must be stable.
 * `onAdd(test)` must not reject: the "+" of that test is disabled until it settles. `ref` reaches the field (see
 * SearchBar), which it can focus. When nothing matches, a row shaped like a result calls `onCreate(term)` with the
 * trimmed term; it is absent without `onCreate`. The current term is searched again when `revision` changes, to
 * show the tests created in the meantime.
 */
const TestSearch = ({
    collectionTests = [],
    languagesOptions = [],
    onSearch = noTests,
    onAdd = noAddition,
    onCreate,
    revision = 0,
    disabled = false,
    ref,
}) => {
    const { t, i18n } = useTranslation('bo');
    const [term, setTerm] = useState('');
    // Last answer, tagged with its term and revision: while another term is searched, nothing of the previous one is shown.
    const [answer, setAnswer] = useState(NO_ANSWER);
    const [adding, setAdding] = useState([]);

    const locale = uiLocale(i18n);
    const searching = term.length >= MIN_TERM_LENGTH;
    const answered = searching && answer.term === term && answer.revision === revision;
    const tests = answered && !answer.failed ? answer.tests.slice(0, RESULT_LIMIT) : [];

    useEffect(() => {
        if (term.length < MIN_TERM_LENGTH) return undefined;
        const controller = new AbortController();
        onSearch(term, { signal: controller.signal }).then(
            (found = []) => {
                if (!controller.signal.aborted) setAnswer({ term, revision, tests: found, failed: false });
            },
            () => {
                if (!controller.signal.aborted) setAnswer({ ...NO_ANSWER, term, revision, failed: true });
            },
        );
        return () => controller.abort();
    }, [term, onSearch, revision]);

    const add = (test) => {
        if (adding.includes(test.reference)) return undefined;
        setAdding((current) => [...current, test.reference]);
        return onAdd(test).finally(() => setAdding((current) => current.filter((reference) => reference !== test.reference)));
    };

    // The "+" of a test, which stays the same, disabled, element once the test is in the collection: the focus is
    // kept after an addition.
    const actionOf = (test, present) => {
        const label = present
            ? t('{{reference}} is already in the collection', { reference: test.reference })
            : t('Add {{name}}', { name: test.reference });
        return (
            <Button
                ghost
                icon={present ? faCheck : faPlus}
                title={label}
                aria-label={label}
                disabled={present || disabled || adding.includes(test.reference)}
                action={() => add(test)}
            />
        );
    };

    const rows = tests.map((test) => {
        const entry = collectionEntry(collectionTests, test.reference);
        const present = Boolean(entry);
        const title = testTitle(test);
        const color = present ? 'neutral-tertiary' : 'neutral-primary';
        const details = [test.level?.name, languageLabel(languagesOptions, test.languageId, locale)].filter(Boolean).join(' · ');
        return (
            <FlexBox key={test.reference} role="listitem" alignItems="center" gap="xs" className={style.row}>
                <FlexBox justifyContent="center" alignItems="center" className={`${style.iconCell} ${present ? style.present : ''}`}>
                    {actionOf(test, present)}
                </FlexBox>
                <Typography fontSize="s" color={color} className={style.reference}>
                    <Highlighted text={test.reference} term={term} color={color} fontSize="s" />
                </Typography>
                <Typography color={color} className={style.title}>
                    {title ? (
                        <Highlighted text={title} term={term} color={color} />
                    ) : (
                        <Typography span color={present ? 'neutral-tertiary' : 'warning-primary'}>
                            {t('Missing title')}
                        </Typography>
                    )}
                    {present && ` · ${t('already in the collection')}`}
                </Typography>
                <Typography fontSize="s" color={present ? 'neutral-tertiary' : 'neutral-secondary'} className={style.details}>
                    {details}
                </Typography>
                <FlexBox className={style.statusCell}>
                    <TestStatusBadge status={entry ? entry.status : test.status} />
                </FlexBox>
            </FlexBox>
        );
    });

    // Outcome of the search, announced by screen readers: the results themselves stay out of the live region.
    let message = null;
    if (searching && !answered) {
        message = <Loader />;
    } else if (answered && answer.failed) {
        message = <Typography color="error-primary">{t('The search failed. Please try again.')}</Typography>;
    } else if (answered && rows.length === 0) {
        message = <Typography color="neutral-secondary">{t('No test matches this search.')}</Typography>;
    } else if (answered && answer.tests.length > RESULT_LIMIT) {
        message = (
            <Typography fontSize="s" color="neutral-secondary">
                {t('Only the first {{shown}} tests are shown: refine the search.', { shown: RESULT_LIMIT })}
            </Typography>
        );
    }

    const showCreate = Boolean(onCreate) && !disabled && answered && !answer.failed && rows.length === 0;
    const createLabel = t('Create the test "{{reference}}"', { reference: term });

    return (
        <FlexBox flexDirection="column" className={style.frame}>
            <SearchBar
                ref={ref}
                embedded
                hideMenu
                compact
                minChars={0}
                debounceMs={300}
                controlShouldRenderValue={false}
                /* eslint-disable-next-line jsx-a11y/no-autofocus */
                autoFocus={false}
                search={async () => []}
                onTextChange={(text) => setTerm(text.trim())}
                onClear={() => setTerm('')}
                disabled={disabled}
                placeholder={t('Add a test: search by reference or title')}
                ariaLabel={t('Add a test')}
            />
            {rows.length > 0 && (
                <FlexBox flexDirection="column" className={style.results}>
                    <FlexBox role="list" aria-label={t('Search results')} flexDirection="column" className={style.list}>
                        {rows}
                    </FlexBox>
                </FlexBox>
            )}
            <FlexBox role="status" aria-live="polite" flexDirection="column" className={style.status}>
                {message}
            </FlexBox>
            {showCreate && (
                <FlexBox flexDirection="column" className={style.results}>
                    <FlexBox role="list" aria-label={t('Create a test')} flexDirection="column" className={style.list}>
                        {/* The whole row creates the test; its "+" does it from the keyboard. */}
                        <FlexBox
                            role="listitem"
                            alignItems="center"
                            gap="xs"
                            className={`${style.row} ${style.createRow}`}
                            onClick={() => onCreate(term)}
                        >
                            {/* The "+" creates the test itself: its click does not reach the row. */}
                            <FlexBox
                                justifyContent="center"
                                alignItems="center"
                                className={style.iconCell}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <Button ghost icon={faPlus} title={createLabel} aria-label={createLabel} action={() => onCreate(term)} />
                            </FlexBox>
                            <Typography fontSize="s" color="neutral-primary" className={style.reference}>
                                {term}
                            </Typography>
                            <Typography color="neutral-primary" className={style.title}>
                                {createLabel}
                            </Typography>
                        </FlexBox>
                    </FlexBox>
                </FlexBox>
            )}
        </FlexBox>
    );
};

export default TestSearch;
